<template>
	<article class="evolution_card">
		<header class="evolution_head">
			<div class="evolution_title_wrap">
				<Ecs class="evolution_icon" />
				<span class="evolution_title">Evolución de Ventas &amp; Márgenes</span>
			</div>
			<!-- Chips de métrica: selección única. Una métrica a la vez porque
				mezclar pesos con porcentajes en un mismo eje no se lee. -->
			<div class="metric_chips">
				<button v-for="metric in metrics" :key="metric.key"
					type="button"
					class="filter_btn"
					:class="{ 'is-active': metric.key === activeMetric }"
					:aria-pressed="String(metric.key === activeMetric)"
					@click="activeMetric = metric.key">
					{{ metric.label }}
				</button>
			</div>
		</header>

		<div class="evolution_canvas">
			<p v-if="!hasData" class="evolution_state">
				Sin datos para el período seleccionado.
			</p>
			<KpiComposedChart v-else
				:monthHeaders="monthLabels"
				:series="series"
				showSymbols />
		</div>
	</article>
</template>

<script>
import Ecs from "@/components/svg/Ecs.vue";
import KpiComposedChart from "@/components/financialReports/charts/KpiComposedChart.vue";
import { dateToHeaderLabel } from "@/views/financialReports/utils/periodFormatting";

// Card "Evolución de Ventas & Márgenes" del drawer de categorías: una línea
// por ítem de la categoría (tienda/marca) sobre los 12 meses, para la métrica
// elegida en los chips. Los valores llegan listos del backend (pesos en
// Ventas, % entero en los márgenes); acá sólo se elige la serie y se formatea.
//
// Reusa `KpiComposedChart` (el del gráfico del tab): resuelve eje, leyenda,
// tooltip por unidad y el color por serie vía `colorToken`. Los tokens son los
// de los progress bars del tema, así una tienda conserva su color entre las
// barras de las cards y su línea acá.
const ITEM_COLOR_TOKENS = [
	"--progress-bar-1",
	"--progress-bar-2",
	"--progress-bar-3",
	"--progress-bar-4",
	"--progress-bar-5",
];

export default {
	name: "ProfitabilityCategoryEvolution",

	components: { Ecs, KpiComposedChart },

	props: {
		// Primeros de mes en ISO, en orden cronológico.
		months: { type: Array, default: () => [] },
		// [{ key, label, unit: 'money'|'percent', series: [{name, values}] }]
		metrics: { type: Array, default: () => [] },
		// Ítems en el orden del backend (define el color de cada uno).
		items: { type: Array, default: () => [] },
	},

	data() {
		return {
			// Métrica activa. La primera es Ventas (la del diseño al abrir).
			activeMetric: this.metrics.length ? this.metrics[0].key : null,
		};
	},

	computed: {
		monthLabels() {
			return this.months.map(dateToHeaderLabel);
		},

		current() {
			return this.metrics.find((metric) => metric.key === this.activeMetric)
				|| this.metrics[0]
				|| null;
		},

		hasData() {
			return Boolean(
				this.months.length && this.current && this.current.series.length,
			);
		},

		// Color por ítem, por nombre: el mismo que las barras de las cards.
		colorByItem() {
			return this.items.reduce((acc, name, i) => {
				acc[name] = ITEM_COLOR_TOKENS[i % ITEM_COLOR_TOKENS.length];
				return acc;
			}, {});
		},

		series() {
			if (!this.current) return [];
			return this.current.series.map((serie) => ({
				name: serie.name,
				type: "line",
				unit: this.current.unit,
				colorToken: this.colorByItem[serie.name],
				data: serie.values,
			}));
		},
	},

	watch: {
		// Cambió la categoría (o el mes): si la métrica activa ya no existe
		// —Servicios no tiene Producto ni Contribución— volvemos a la primera.
		metrics(list) {
			if (!list.some((metric) => metric.key === this.activeMetric)) {
				this.activeMetric = list.length ? list[0].key : null;
			}
		},
	},
};
</script>

<style scoped lang="scss">
// Misma chrome que las cards del reporte (surface + borde + radio 16).
.evolution_card {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 16px;
	box-shadow: var(--report-shadow-card);
	padding: 12px 16px 16px;
	display: flex;
	flex-direction: column;
}

.evolution_head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 8px;
}

.evolution_title_wrap {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
}

.evolution_icon {
	width: 18px;
	height: 18px;
	flex-shrink: 0;
	color: var(--report-text-muted);
}

.evolution_title {
	font-size: 16px;
	font-weight: 600;
	color: var(--report-text);
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.metric_chips {
	display: flex;
	align-items: center;
	gap: 4px;
	flex-shrink: 0;
	flex-wrap: wrap;
	justify-content: flex-end;
}

// Mismo pill que los sub-filtros del header del tab.
.filter_btn {
	background: transparent;
	border: 1px solid transparent;
	border-radius: 8px;
	padding: 4px 10px;
	font-size: 12px;
	font-weight: 500;
	color: var(--report-text-muted);
	cursor: pointer;
	transition: background-color 160ms ease, color 160ms ease;

	&:hover {
		background: var(--report-surface);
		color: var(--report-text);
	}

	&:focus-visible {
		outline: 2px solid var(--report-accent-border);
		outline-offset: 2px;
	}

	&.is-active {
		background: var(--report-accent-bg);
		border-color: var(--report-accent-border);
		color: var(--report-accent-text);
	}
}

.evolution_canvas {
	height: 320px;
	min-height: 0;
	display: flex;
	align-items: stretch;
	justify-content: stretch;
	position: relative;
}

.evolution_state {
	margin: auto;
	color: var(--report-text-muted);
	font-size: 13px;
	text-align: center;
}
</style>
